import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

export interface PendingProfileUser {
    workdayId: string;
    fullName: string;
    status: string;
    supervisorId?: string;
    supervisorNm?: string;
    skills?: string[];
    teamCd?: string;
}

export interface ProfileAdminSummaryCounts {
    totalRegistered: number;
    activeUsers: number;
    pendingUsers: number;
    pendingDirectReports: number;
}

interface ProfileAdminSummaryState {
    counts: ProfileAdminSummaryCounts;
    allUsers: PendingProfileUser[];
    loading: boolean;
    error: string | null;
}

const initialState: ProfileAdminSummaryState = {
    counts: {
        totalRegistered: 0,
        activeUsers: 0,
        pendingUsers: 0,
        pendingDirectReports: 0,
    },
    allUsers: [],
    loading: false,
    error: null,
};

const normalizePendingUser = (item: any): PendingProfileUser | null => {
    if (!item) return null;

    const workdayId = String(item.workdayId || '').trim();
    if (!workdayId) return null;

    const fullName = String(item.fullName || 'Unknown User').trim() || 'Unknown User';
    const status = String(item.status || 'PND').trim();
    const supervisorId = String(item.supervisorId || '').trim() || undefined;
    const supervisorNm = String(item.supervisorNm || '').trim() || undefined;
    const skills = Array.isArray(item.skills) ? item.skills : [];
    const teamCd = String(item.teamCd || '').trim() || undefined;

    return { workdayId, fullName, status, supervisorId, supervisorNm, skills, teamCd };
};

const isActiveStatus = (status: string): boolean => {
    const normalized = status.toUpperCase();
    return normalized === 'ACT' || normalized === 'ACTIVE';
};

const isPendingStatus = (status: string): boolean => {
    const normalized = status.toUpperCase();
    return normalized === 'PND' || normalized === 'PENDING';
};

const mapSummaryResponse = (raw: any): { counts: ProfileAdminSummaryCounts; allUsers: PendingProfileUser[] } => {
    // API returns a flat list of ProfileAdminSummaryUser objects
    let usersList: any[] = [];
    if (Array.isArray(raw)) {
        usersList = raw;
    } else if (Array.isArray(raw?.data)) {
        usersList = raw.data;
    }

    const allUsers = usersList
        .map(normalizePendingUser)
        .filter((user): user is PendingProfileUser => Boolean(user))
        .filter((user) => user.teamCd !== 'A');

    const pendingUsersList = allUsers.filter((user) => isPendingStatus(user.status));
    const activeUsers = allUsers.filter((user) => isActiveStatus(user.status)).length;
    const totalRegistered = allUsers.length;
    // Read pendingDirectReports from API response if provided; falls back to 0 until backend supplies it
    const rawPdr = raw?.pendingDirectReports ?? raw?.data?.pendingDirectReports;
    const pendingDirectReports = (typeof rawPdr === 'number' && Number.isFinite(rawPdr)) ? rawPdr : 0;

    const counts: ProfileAdminSummaryCounts = {
        totalRegistered,
        activeUsers,
        pendingUsers: pendingUsersList.length,
        pendingDirectReports,
    };

    return { counts, allUsers };
};

export const fetchProfileAdminSummary = createAsyncThunk(
    'profileAdminSummary/fetchProfileAdminSummary',
    async ({ siteCd }: { siteCd: string }, { rejectWithValue }) => {
        try {
            const url = `${ENDPOINTS.GET_PROFILE_ADMIN_SUMMARY}?siteCd=${siteCd}`;
            const response = await API_FETCH(url);
            return mapSummaryResponse(response);
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch profile admin summary data');
        }
    }
);

const profileAdminSummarySlice = createSlice({
    name: 'profileAdminSummary',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProfileAdminSummary.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProfileAdminSummary.fulfilled, (state, action) => {
                state.loading = false;
                state.counts = action.payload.counts;
                state.allUsers = action.payload.allUsers;
            })
            .addCase(fetchProfileAdminSummary.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = profileAdminSummarySlice.actions;
export default profileAdminSummarySlice.reducer;
